import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Work = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [hoveredProject, setHoveredProject] = useState(null);

  const projects = [
    {
      id: 1,
      title: "Pearl—AI-Powered Dental Platform",
      description: "Leading product growth for innovative AI dental diagnostics platform, driving category-defining dental solutions.",
      category: "Product Leadership",
      tags: ["AI/ML", "Healthcare", "Product Strategy", "Growth"],
      image: "/api/placeholder/600/400",
      period: "2024 - Present",
      role: "Lead Product Manager, Growth"
    },
    {
      id: 2,
      title: "Hifi—DeFi Lending Platform",
      description: "Headed product development for cutting-edge DeFi solutions, transforming user insights into scalable financial products.",
      category: "Product Management",
      tags: ["Web3", "Product Strategy", "User Experience", "Marketing"],
      image: "/api/placeholder/600/400",
      period: "2022 - 2024",
      role: "Head of Product"
    },
    {
      id: 3,
      title: "Via—E-commerce Text Marketing",
      description: "Drove product initiatives for e-commerce text marketing solutions, optimizing user experience and operational efficiency.",
      category: "Product Strategy",
      tags: ["E-commerce", "Integrations", "Text Marketing", "Compliance"],
      image: "/api/placeholder/600/400",
      period: "2021 - 2022",
      role: "Senior Product Manager"
    },
    {
      id: 4,
      title: "Weave—Communication Platform",
      description: "Managed a communication platform that enhanced customer engagement and streamlined workflows.",
      category: "Product Development",
      tags: ["SaaS", "Automation", "Scaling", "Workflow"],
      image: "/api/placeholder/600/400",
      period: "2016 - 2021",
      role: "Group Product Manager"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="work" className="py-24 bg-white">
      <div className="container-max section-padding">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          <motion.div className="text-center mb-16" variants={cardVariants}>
            <h2 className="text-4xl md:text-5xl font-bold text-text-main mb-6">
              Work
            </h2>
            <p className="text-xl text-secondary-accent max-w-3xl mx-auto leading-relaxed">
              From insight to impact. Here are some of the projects I've worked on.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            variants={containerVariants}
          >
            {projects.map((project) => (
              <motion.div
                key={project.id}
                className="group cursor-pointer"
                variants={cardVariants}
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                onMouseEnter={() => setHoveredProject(project.id)}
                onMouseLeave={() => setHoveredProject(null)}
              >
                <div className="bg-subtle-accent rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300">
                  <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                    {project.id === 1 ? (
                      // Pearl project with video
                      <>
                        <video
                          className="absolute inset-0 w-full h-full object-contain bg-white"
                          ref={(video) => {
                            if (video) {
                              if (hoveredProject === 1) {
                                video.play();
                              } else {
                                video.pause();
                                video.currentTime = 0;
                              }
                            }
                          }}
                          muted
                          loop
                          playsInline
                          preload="metadata"
                        >
                          <source src="/Tooth Animation.mp4" type="video/mp4" />
                        </video>
                      </>
                    ) : project.id === 2 ? (
                      // Hifi project with video
                      <>
                        <video
                          className="absolute inset-0 w-full h-full object-contain bg-white"
                          ref={(video) => {
                            if (video) {
                              if (hoveredProject === 2) {
                                video.play();
                              } else {
                                video.pause();
                                video.currentTime = 0;
                              }
                            }
                          }}
                          muted
                          loop
                          playsInline
                          preload="metadata"
                        >
                          <source src="/DeFi Animation.mp4" type="video/mp4" />
                        </video>
                      </>
                    ) : project.id === 3 ? (
                      // Via project with video
                      <>
                        <video
                          className="absolute inset-0 w-full h-full object-contain bg-white"
                          ref={(video) => {
                            if (video) {
                              if (hoveredProject === 3) {
                                video.play();
                              } else {
                                video.pause();
                                video.currentTime = 0;
                              }
                            }
                          }}
                          muted
                          loop
                          playsInline
                          preload="metadata"
                        >
                          <source src="/e-comm Animation.mp4" type="video/mp4" />
                        </video>
                      </>
                    ) : (
                      // Weave project with video
                      <>
                        <video
                          className="absolute inset-0 w-full h-full object-contain bg-white"
                          ref={(video) => {
                            if (video) {
                              if (hoveredProject === 4) {
                                video.play();
                              } else {
                                video.pause();
                                video.currentTime = 0;
                              }
                            }
                          }}
                          muted
                          loop
                          playsInline
                          preload="metadata"
                        >
                          <source src="/Texting Animation.mp4" type="video/mp4" />
                        </video>
                      </>
                    )}
                  </div>
                  
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-text-main mb-3 group-hover:text-primary-accent transition-colors duration-200">
                      {project.title}
                    </h3>
                    
                    <p className="text-secondary-accent mb-4 leading-relaxed">
                      {project.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs font-medium text-secondary-accent bg-white px-2 py-1 rounded-md"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Work; 